import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Box, Container, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import { useGetCompetitorProperties } from '~/hooks/apis/price-parity/useGetCompetitorProperties';

import CompetitorOfferSupplierPanel from './CompetitorOfferSupplierPanel';
import { COMPETITOR_OFFERS_PATH } from './constants';
import { competitorOfferPath, hotelSuppliers, supplierKey, supplierLabel } from './utils';

export default function CompetitorOfferDetailPage() {
  const { sourceName, externalPropertyId } = useParams<{ sourceName: string; externalPropertyId: string }>();
  // The list is cached, so the detail page reads the hotel from it rather than a separate endpoint.
  const { data: properties = [], isLoading, error } = useGetCompetitorProperties();
  const property = properties.find(
    (candidate) => candidate.sourceName === sourceName && candidate.externalPropertyId === externalPropertyId,
  );

  if (isLoading) {
    return <Spinner />;
  }

  const suppliers = property ? hotelSuppliers(properties, property) : [];

  return (
    <Container>
      <Helmet>
        <title>Competitor Offers{property?.hotelName ? ` - ${property.hotelName}` : ''}</title>
      </Helmet>
      <PageHeader title={property?.hotelName ?? 'Competitor offer'} backButton={COMPETITOR_OFFERS_PATH} />

      {error && <Alert severity="error">Failed to load competitor offers: {error.message}</Alert>}
      {!error && !property && <Alert severity="warning">This hotel is not in the competitor collection.</Alert>}

      {property && (
        <>
          <Box borderBottom={1} borderColor="divider" mb={3}>
            <Tabs value={supplierKey(property)} variant="scrollable">
              {suppliers.map((supplier) => (
                <Tab
                  key={supplierKey(supplier)}
                  value={supplierKey(supplier)}
                  label={supplierLabel(supplier, suppliers)}
                  component={Link}
                  to={competitorOfferPath(supplier)}
                />
              ))}
            </Tabs>
          </Box>
          <CompetitorOfferSupplierPanel key={supplierKey(property)} property={property} />
        </>
      )}
    </Container>
  );
}
